import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { chartsGridClasses } from '@mui/x-charts/ChartsGrid';
import { HEALTH_STATUS, STATUS_COLORS } from 'models/fleet-management/FleetWidget';
import { MissionCodeType } from 'models/enums/fleet-management';

/** Vertical rhythm between the page sections. */
export const FLEET_SECTION_GAP = 3;

export const CHART_GRID = '#E7EAEF';
export const CHART_AXIS = '#C9CED6';
export const CHART_TICK_LABEL = '#6B7280';

/**
 * One hue, light to dark, stepped off the brand blue. Index 0 is the lightest step that still
 * clears 2:1 against a white chart surface, so the whole ramp is usable for ordinal marks.
 */
export const BLUE_RAMP = ['#6EBBF8', '#39A2F5', '#1086E4', '#0D6EBB', '#0A5794', '#084270'];

/** The accent for a single series chart or meter. */
export const CHART_SERIES_BLUE = BLUE_RAMP[2];

/**
 * The health statuses as chart marks. Green, orange and red are the status palette unchanged;
 * grey is lightened because `STATUS_COLORS.GREY` is a near black - right for text and a chip
 * outline, far too heavy as a filled segment sitting next to three real hues.
 */
export const HEALTH_MARK_COLORS: Record<Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL>, string> = {
  [HEALTH_STATUS.GREEN]: STATUS_COLORS.GREEN,
  [HEALTH_STATUS.ORANGE]: STATUS_COLORS.ORANGE,
  [HEALTH_STATUS.RED]: STATUS_COLORS.RED,
  [HEALTH_STATUS.GREY]: '#98A2B3',
};

/**
 * A health status as a whole surface, not only a dot: `mark` fills a segment or a bar, `tint`
 * backs a number so the bucket is recognised before the label is read, and `ink` is the step
 * dark enough to be read as text on that tint and on white. Every ink clears 6:1 on white.
 */
export interface StatusTone {
  mark: string;
  tint: string;
  ink: string;
}

export const HEALTH_TONES: Record<Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL>, StatusTone> = {
  [HEALTH_STATUS.GREEN]: {
    mark: HEALTH_MARK_COLORS[HEALTH_STATUS.GREEN],
    tint: '#E8F5EE',
    ink: '#1B6E43',
  },
  [HEALTH_STATUS.ORANGE]: {
    mark: HEALTH_MARK_COLORS[HEALTH_STATUS.ORANGE],
    tint: '#FDF3DE',
    ink: '#8A5203',
  },
  [HEALTH_STATUS.RED]: {
    mark: HEALTH_MARK_COLORS[HEALTH_STATUS.RED],
    tint: '#FDE9E8',
    ink: '#B3201C',
  },
  [HEALTH_STATUS.GREY]: {
    mark: HEALTH_MARK_COLORS[HEALTH_STATUS.GREY],
    tint: '#F2F4F7',
    ink: '#475467',
  },
};

/**
 * Performance direction wears its own hue. Green and red mean health status everywhere else in
 * Insights, so a robot that cleans less than it used to must not read as a red-status robot.
 */
export const PERFORMANCE_TONE: StatusTone = {
  mark: '#5B5BD6',
  tint: '#EDEDFB',
  ink: '#3E3EA8',
};

/** Errors read in the red ink and maintenance in the orange, the way the robot page shows them. */
export const CODE_TONES: Record<MissionCodeType, StatusTone> = {
  [MissionCodeType.ERROR]: HEALTH_TONES[HEALTH_STATUS.RED],
  [MissionCodeType.MAINTENANCE]: HEALTH_TONES[HEALTH_STATUS.ORANGE],
};

/** Codes are identifiers, so they are set in a fixed-width face and line up down a column. */
export const CODE_FONT = "'Roboto Mono', monospace";

/** The translation keys naming each health status, in the order they are always shown. */
export const HEALTH_STATUS_LABEL_KEYS: Record<Exclude<HEALTH_STATUS, HEALTH_STATUS.ALL>, string> = {
  [HEALTH_STATUS.GREEN]: 'fleet-management-health-label-green',
  [HEALTH_STATUS.ORANGE]: 'fleet-management-health-label-orange',
  [HEALTH_STATUS.RED]: 'fleet-management-health-label-red',
  [HEALTH_STATUS.GREY]: 'fleet-management-health-label-grey',
};

export const HEALTH_STATUS_ORDER = [
  HEALTH_STATUS.GREEN,
  HEALTH_STATUS.ORANGE,
  HEALTH_STATUS.RED,
  HEALTH_STATUS.GREY,
] as const;

/** Recessive axes and a solid hairline grid, one shade off the surface. */
export const chartSx = {
  [`.${axisClasses.line}`]: { stroke: CHART_AXIS },
  [`.${axisClasses.tick}`]: { stroke: CHART_AXIS },
  [`.${axisClasses.tickLabel}`]: { fill: CHART_TICK_LABEL, fontSize: 12 },
  [`.${chartsGridClasses.line}`]: { stroke: CHART_GRID, strokeDasharray: 'none' },
};

/** Room for rotated week labels below the plot and for the y axis ticks beside it. */
export const WEEKLY_CHART_MARGIN = { left: 68, right: 16, top: 16, bottom: 72 };

/** Week labels are long, so they lean rather than overlap. */
export const weeklyTickLabelStyle = { angle: -35, textAnchor: 'end' as const, fontSize: 12 };

/**
 * Keeps a category list readable: the biggest `limit` entries in order, everything behind them
 * folded into one "other" entry instead of becoming another colour on the chart.
 */
export const topNWithOther = (
  entries: { label: string; value: number }[],
  limit: number,
  otherLabel: string,
): { label: string; value: number }[] => {
  const sorted = [...entries].sort((a, b) => b.value - a.value);
  if (sorted.length <= limit) return sorted;

  const tail = sorted.slice(limit).reduce((sum, entry) => sum + entry.value, 0);
  const head = sorted.slice(0, limit);
  return tail > 0 ? [...head, { label: otherLabel, value: tail }] : head;
};
